// LivestreamDetailOnlineFooterMobile.jsx
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  PRESET_PREVIEW,
  privateShowStatus as privateShowConstants,
  LivestreamUIMode,
} from '../resource/liveStreamConstants.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import useClickWithTouchBackfill from '../hook/useClickWithTouchBackfill.js';

import UniversalComponent from '../component/UniversalComponent.jsx';
import Tooltip from '../component/Tooltip.jsx';
import LiveStreamSettingButton from '../container/LiveStreamSettingButton.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import {
  headerHeight,
  iPhoneXSeriesFooterPaddingInPWA,
  color,
} from '../style/variables.js';
import media, { mediaHeight } from '../style/media.js';

import GiftIconSrc from '../../img/icon-gift.png';
import ToyCommandIconSrc from '../../img/icon-toy-command.png';
import PrivateCtaIconSrc from '../../img/icon-private-cta.png';
import SendIconSrc from '../../img/ic-send.svg';
import CleanModeIconSrc from '../../img/ic-clean-mode.svg';

import AudioOnIconSrc from '../../img/icon-audio-fill-shadow.svg';
import AudioOffIconSrc from '../../img/icon-mute-fill-shadow.svg';

const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();
const TRIGGER_PRIVATE_BUTTON_MOUNT_ID =
  'trigger-private-button-livestream-detail-online-footer-mobile';
const GIFT_BUTTON_MOUNT_ID =
  'livestream-gift-button-livestream-detail-online-footer-mobile';
const KARAOKE_BUTTON_MOUNT_ID =
  'karaoke-button-livestream-detail-online-footer-mobile';
const SETTING_BUTTON_MOUNT_ID = 'livestream-setting-button-mobile';

const zIndex = {
  backdrop: 2,
  chatPanel: 3,
};

export const LivestreamDetailOnlineFooterMobile = ({
  streamId = null,
  streamStatus = null,
  streamPreviewBlurDuration = 30,
  isAuthed = false,
  isStreamActivated = false,
  isLiveStreaming = null,
  isStreamer = false,
  isChatPanelOpen = false,
  isGiftModalOpen = false,
  isKaraokeModalOpen = false,
  isTriggerPrivateModalOpen = false,
  isSuperChatOpen = false,
  hasStreamToken = false,
  shouldDisplayBlurCountdown = false,
  shouldUseLivestreamAlert = false,
  settingHintOpenedTimestamp = 0,
  privateShowStatus = null,
  isKaraokeDeviceOnline = false,
  isMuted = false,
  isExclusivePrivateOngoing = false,
  isNormalMode = false,
  isCleanMode = false,
  setMuted = NULL_FUNCTION,
  login = NULL_FUNCTION,
  setChatPanel = NULL_FUNCTION,
  toggleModal = NULL_FUNCTION,
  checkLivestreamChatPreflightAndOpenPanel = NULL_FUNCTION,
  setSettingHintOpenedTimestamp = NULL_FUNCTION,
  handleSlideChange = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const shouldDisplayChatButton =
    isAuthed &&
    isLiveStreaming &&
    (streamStatus === PRESET_PREVIEW || hasStreamToken);

  const handleCleanModeOnClick = useCallback(() => {
    handleSlideChange(LivestreamUIMode.NORMAL);
  }, [handleSlideChange]);

  const handleMuteOnClick = useCallback(
    () => setMuted({ isMuted: !isMuted, isFirstUnmuted: false }),
    [setMuted, isMuted]
  );

  // Event handlers
  const toggleChatPanel = useCallback(() => {
    if (!isAuthed) {
      login();
    } else if (isStreamer) {
      setChatPanel({ isChatPanelOpen: !isChatPanelOpen });
    } else if (isChatPanelOpen) {
      setChatPanel({ isChatPanelOpen: false });
    } else {
      checkLivestreamChatPreflightAndOpenPanel({ streamId });
    }
  }, [
    isAuthed,
    isChatPanelOpen,
    isStreamer,
    login,
    streamId,
    setChatPanel,
    checkLivestreamChatPreflightAndOpenPanel,
  ]);

  const toggleKaraokeModal = useCallback(() => {
    toggleModal({
      modalId: 'LiveStreamKaraokeModal',
      payload: {
        mountElementId: isStreamActivated ? KARAOKE_BUTTON_MOUNT_ID : '',
        streamId,
      },
      isOpen: !isKaraokeModalOpen,
    });
  }, [isStreamActivated, isKaraokeModalOpen, streamId, toggleModal]);

  const toggleTriggerPrivateModal = useCallback(() => {
    toggleModal({
      modalId: 'LiveStreamTriggerPrivateModal',
      payload: {
        streamId,
        mountElementId: isStreamActivated
          ? TRIGGER_PRIVATE_BUTTON_MOUNT_ID
          : '',
      },
      isOpen: !isTriggerPrivateModalOpen,
    });
  }, [isStreamActivated, isTriggerPrivateModalOpen, toggleModal, streamId]);

  const toggleLiveStreamGiftModal = useCallback(() => {
    toggleModal({
      modalId: 'LiveStreamGiftModal',
      payload: {
        streamId,
        mountElementId: isStreamActivated ? GIFT_BUTTON_MOUNT_ID : '',
      },
      isOpen: !isGiftModalOpen,
    });
  }, [isGiftModalOpen, isStreamActivated, streamId, toggleModal]);

  const handleChatInputClick = useCallback(() => {
    if (!shouldDisplayChatButton) return;
    if (!isAuthed && streamStatus === PRESET_PREVIEW) {
      login();
    } else {
      toggleChatPanel();
    }
  }, [isAuthed, login, shouldDisplayChatButton, streamStatus, toggleChatPanel]);

  const chatInputClickProps = useClickWithTouchBackfill({
    onClick: handleChatInputClick,
  });
  const giftClickProps = useClickWithTouchBackfill({
    onClick: toggleLiveStreamGiftModal,
  });
  const privateCtaClickProps = useClickWithTouchBackfill({
    onClick: toggleTriggerPrivateModal,
  });
  const karaokeClickProps = useClickWithTouchBackfill({
    onClick: toggleKaraokeModal,
  });
  const cleanModeClickProps = useClickWithTouchBackfill({
    onClick: handleCleanModeOnClick,
  });
  const muteClickProps = useClickWithTouchBackfill({
    onClick: handleMuteOnClick,
  });
  const chatBackdropClickProps = useClickWithTouchBackfill({
    onClick: toggleChatPanel,
  });

  const renderChatInput = useCallback(() => {
    return (
      <ChatInputDisplay
        $isHidden={isChatPanelOpen}
        $shouldDisplay={shouldDisplayChatButton}
        {...chatInputClickProps}
      >
        <ChatInputText>
          {t(
            shouldUseLivestreamAlert
              ? 'livestream_chat_notify_input'
              : 'chat_message_placeholder_say_hello',
            { ns: TranslationNamespace.LIVESTREAM }
          )}
        </ChatInputText>
        <ChatSendIcon>
          <img src={SendIconSrc} alt="Send" />
        </ChatSendIcon>
      </ChatInputDisplay>
    );
  }, [
    shouldDisplayChatButton,
    isChatPanelOpen,
    chatInputClickProps,
    shouldUseLivestreamAlert,
    t,
  ]);

  const renderGiftButton = useCallback(
    () => (
      <GiftButton
        data-element_id={ButtonId.All.ButtonGift}
        data-sheet_mount_id={isStreamActivated ? GIFT_BUTTON_MOUNT_ID : ''}
        {...giftClickProps}
      >
        <img src={GiftIconSrc} alt="Gift" />
      </GiftButton>
    ),
    [giftClickProps, isStreamActivated]
  );

  const isShouldDisplayPrivateCta = useMemo(
    () =>
      (streamStatus === PRESET_PREVIEW || hasStreamToken) &&
      !isExclusivePrivateOngoing,
    [hasStreamToken, isExclusivePrivateOngoing, streamStatus]
  );

  const renderPrivateCtaButton = useCallback(() => {
    if (!isShouldDisplayPrivateCta) return null;

    return (
      <PrivateCtaButton
        $isActive={isTriggerPrivateModalOpen}
        data-element_id={ButtonId.LiveStream.ButtonUserTrigger}
        data-sheet_mount_id={
          isStreamActivated ? TRIGGER_PRIVATE_BUTTON_MOUNT_ID : ''
        }
        {...privateCtaClickProps}
      >
        <img src={PrivateCtaIconSrc} alt="Private CTA" />
      </PrivateCtaButton>
    );
  }, [
    isShouldDisplayPrivateCta,
    isTriggerPrivateModalOpen,
    isStreamActivated,
    privateCtaClickProps,
  ]);

  const renderToyCommandButton = useCallback(() => {
    const shouldStopKaraokeIfPerforming = [
      privateShowConstants.PERFORMING,
      privateShowConstants.PREPARING,
    ].includes(privateShowStatus);

    const shouldHideToyButton =
      shouldStopKaraokeIfPerforming && !isKaraokeDeviceOnline;

    return (
      <ToyCommandButtonWrapper $shouldHide={shouldHideToyButton}>
        <ToyCommandButton
          data-element_id={ButtonId.LiveStream.ButtonCommandList}
          data-sheet_mount_id={isStreamActivated ? KARAOKE_BUTTON_MOUNT_ID : ''}
          {...karaokeClickProps}
        >
          <ToyCommandIconContainer>
            <img src={ToyCommandIconSrc} alt="Toy Command" />
          </ToyCommandIconContainer>
        </ToyCommandButton>
      </ToyCommandButtonWrapper>
    );
  }, [
    isKaraokeDeviceOnline,
    isStreamActivated,
    privateShowStatus,
    karaokeClickProps,
  ]);

  const shouldShowSettingHint = !settingHintOpenedTimestamp && isNormalMode;
  const renderSettingHint = useCallback(() => {
    if (!shouldShowSettingHint) return null;
    return (
      <SettingTooltipWrapper>
        {t('tips_livestream_update_settings', {
          ns: TranslationNamespace.LIVESTREAM,
        })}
      </SettingTooltipWrapper>
    );
  }, [shouldShowSettingHint, t]);

  const handleSettingHintClick = useCallback(() => {
    if (!shouldShowSettingHint) return;
    toggleModal({
      modalId: 'LiveStreamSettingHintModal',
      payload: {
        streamId,
        mountElementId: isStreamActivated ? SETTING_BUTTON_MOUNT_ID : '',
      },
      isOpen: true,
    });
    setSettingHintOpenedTimestamp({
      settingHintOpenedTimestamp: Date.now(),
    });
  }, [
    isStreamActivated,
    streamId,
    shouldShowSettingHint,
    setSettingHintOpenedTimestamp,
    toggleModal,
  ]);

  const renderSettingButton = useCallback(() => {
    return (
      <HintButtonWrapper $shouldHide={!isNormalMode && !shouldShowSettingHint}>
        <Tooltip
          key={
            shouldShowSettingHint
              ? 'show-setting-tooltip'
              : 'hide-setting-tooltip'
          }
          position="top-left"
          trigger="auto"
          renderHint={renderSettingHint}
          onHintClosed={handleSettingHintClick}
        >
          <LiveStreamSettingButton
            streamId={streamId}
            isActive={isStreamActivated}
            mountElementId={isStreamActivated ? SETTING_BUTTON_MOUNT_ID : ''}
          />
        </Tooltip>
      </HintButtonWrapper>
    );
  }, [
    isNormalMode,
    isStreamActivated,
    streamId,
    shouldShowSettingHint,
    handleSettingHintClick,
    renderSettingHint,
  ]);

  const CleanModeButton = useMemo(
    () => (
      <StyledCleanModeButton
        data-element_id={ButtonId.All.ButtonTheaterMode}
        {...cleanModeClickProps}
      >
        <img src={CleanModeIconSrc} alt="TheaterMode" />
      </StyledCleanModeButton>
    ),
    [cleanModeClickProps]
  );

  const MuteButton = useMemo(
    () => (
      <StyledMuteButton
        data-element_id={
          isMuted ? ButtonId.All.ButtonMuted : ButtonId.All.ButtonVolume
        }
        {...muteClickProps}
      >
        <img src={isMuted ? AudioOffIconSrc : AudioOnIconSrc} alt="Mute" />
      </StyledMuteButton>
    ),
    [muteClickProps, isMuted]
  );

  const ControlPanelComponent = useMemo(
    () => (
      <ControlPanel>
        <ControlPanelLeftContainer $isFlexShrink={isCleanMode}>
          {isCleanMode && CleanModeButton}
          {MuteButton}
          {isNormalMode && renderSettingButton()}
          {isNormalMode && renderChatInput()}
        </ControlPanelLeftContainer>
        {shouldDisplayBlurCountdown && isStreamActivated && (
          <UniversalComponent
            filename="Component_LivestreamPreviewBlurCountdown"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../component/LivestreamPreviewBlurCountdown.jsx')
            }
            streamPreviewBlurDuration={streamPreviewBlurDuration}
          />
        )}
        {renderGiftButton()}
        {renderPrivateCtaButton()}
        {renderToyCommandButton()}
      </ControlPanel>
    ),
    [
      isCleanMode,
      CleanModeButton,
      MuteButton,
      isNormalMode,
      isStreamActivated,
      shouldDisplayBlurCountdown,
      streamPreviewBlurDuration,
      renderChatInput,
      renderGiftButton,
      renderPrivateCtaButton,
      renderSettingButton,
      renderToyCommandButton,
    ]
  );
  const ChatPanelComponent = useMemo(() => {
    return (
      <>
        <ChatBackDrop {...chatBackdropClickProps} />
        <MobileAndTabletChatPanelContainer className="swiper-no-swiping">
          {isSuperChatOpen && (
            <SuperChatPanel>
              <UniversalComponent
                filename="Container_LivestreamSuperChatPanel"
                streamId={streamId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LivestreamSuperChatPanel.js')
                }
              />
            </SuperChatPanel>
          )}
          <ChatPanel>
            <ChatPanelInputWrapper>
              <UniversalComponent
                filename="Container_LiveStreamChatInput"
                streamId={streamId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LiveStreamChatInput.js')
                }
              />
            </ChatPanelInputWrapper>
          </ChatPanel>
        </MobileAndTabletChatPanelContainer>
      </>
    );
  }, [chatBackdropClickProps, isSuperChatOpen, streamId]);

  return (
    <StyledLivestreamDetailOnlineFooterMobile>
      {!isChatPanelOpen && ControlPanelComponent}
      {isNormalMode &&
        isChatPanelOpen &&
        isStreamActivated &&
        ChatPanelComponent}
    </StyledLivestreamDetailOnlineFooterMobile>
  );
};

LivestreamDetailOnlineFooterMobile.propTypes = {
  streamId: PropTypes.string,
  streamStatus: PropTypes.string,
  streamPreviewBlurDuration: PropTypes.number,
  isAuthed: PropTypes.bool,
  isStreamActivated: PropTypes.bool,
  isLiveStreaming: PropTypes.bool,
  isStreamer: PropTypes.bool,
  isChatPanelOpen: PropTypes.bool,
  isGiftModalOpen: PropTypes.bool,
  isKaraokeModalOpen: PropTypes.bool,
  isTriggerPrivateModalOpen: PropTypes.bool,
  isSuperChatOpen: PropTypes.bool,
  hasStreamToken: PropTypes.bool,
  shouldDisplayBlurCountdown: PropTypes.bool,
  settingHintOpenedTimestamp: PropTypes.number,
  shouldUseLivestreamAlert: PropTypes.bool,
  privateShowStatus: PropTypes.string,
  isKaraokeDeviceOnline: PropTypes.bool,
  isMuted: PropTypes.bool,
  isExclusivePrivateOngoing: PropTypes.bool,
  setMuted: PropTypes.func,
  login: PropTypes.func,
  setChatPanel: PropTypes.func,
  toggleModal: PropTypes.func,
  checkLivestreamChatPreflightAndOpenPanel: PropTypes.func,
  setSettingHintOpenedTimestamp: PropTypes.func,
  handleSlideChange: PropTypes.func,
  isNormalMode: PropTypes.bool,
  isCleanMode: PropTypes.bool,
};

const StyledLivestreamDetailOnlineFooterMobile = styled.div`
  position: relative;
  z-index: 2; //ensure footer has higher z-index than LivstreamAside
  display: none;
  height: calc(
    max(16px, ${isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0}px) +
      40px
  );
  ${media.tablet`
    display: block;
    min-height: ${isIphoneXSeriesPWA ? 78 : 60}px; // the height of the control panel
  `}
`;

const ControlPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 0px 8px
    max(16px, ${isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0}px);
`;

const ControlButton = styled.button`
  ${ResetButtonStyle};
  max-width: 40px;
  max-height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: none;
  padding: 6px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: rgba(25, 25, 25, 0.2);
  touch-action: manipulation;

  > img {
    object-fit: contain;
    pointer-events: none;
  }
  :last-child {
    margin-bottom: 0px;
  }
  ${media.tablet`
    display: inline-flex;
  `};
`;

const GiftButton = styled(ControlButton)`
  position: relative;
  > img {
    width: 100%;
    height: 100%;
  }
`;

const PrivateCtaButton = styled(ControlButton)`
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 100%;
    height: 100%;
  }
`;

const ToyCommandButton = styled(ControlButton)`
  position: relative;
`;

const StyledCleanModeButton = styled(ControlButton)`
  > img {
    width: 100%;
    height: 100%;
  }
`;

const StyledMuteButton = styled(ControlButton)`
  > img {
    width: 100%;
    height: 100%;
  }
`;

const HintButtonWrapper = styled.div`
  display: none;
  position: relative;
  align-items: center;
  justify-content: center;

  ${props =>
    props.$shouldHide &&
    css`
      display: none;
    `}

  ${media.tablet`
    display: inline-flex;
    ${props =>
      props.$shouldHide &&
      css`
        display: none;
      `}
  `};
`;

const ToyCommandButtonWrapper = styled(HintButtonWrapper)``;

const ChatInputDisplay = styled.div`
  flex: 1;
  min-width: 0;
  max-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  height: 40px;
  border-radius: 20px;
  border: solid 1px ${color.neutral['30']};
  background-color: rgba(25, 25, 25, 0.2);
  touch-action: manipulation;

  visibility: hidden;
  pointer-events: none;

  ${media.tablet`
    cursor: ${({ $shouldDisplay }) => ($shouldDisplay ? 'pointer' : 'default')};
    visibility: ${({ $isHidden, $shouldDisplay }) =>
      $isHidden || !$shouldDisplay ? 'hidden' : 'visible'};
    pointer-events: ${({ $isHidden, $shouldDisplay }) =>
      $isHidden || !$shouldDisplay ? 'none' : 'auto'};
    opacity: ${({ $shouldDisplay }) => ($shouldDisplay ? '1' : '0')};
  `}

  ${mediaHeight.mobileLandscape`
    @media (orientation: landscape) and (hover: none) {
      flex: none;
      width: 124px;
    }
  `};
`;

const ChatInputText = styled.div`
  flex: 1;
  color: ${color.neutral['100_alpha_40']};
  font-size: 14px;
  user-select: none;
`;

const ChatSendIcon = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const ControlPanelLeftContainer = styled.div`
  flex: ${({ $isFlexShrink }) => ($isFlexShrink ? '1' : 'none')};
  display: flex;
  flex: 1;
  gap: 4px;
`;

const TooltipHintWrapper = styled.div`
  position: relative;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.neutral[100]};
  background-color: ${color.neutral[0]};
  text-align: center;
  display: inline-block;
  width: max-content;
  max-width: 250px;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  box-shadow: 0px 2px 8px 0px rgba(25, 25, 25, 0.2);
  &::after {
    position: absolute;
    bottom: -4px;
    right: 20px;
    width: 8px;
    height: 8px;
    background-color: ${color.neutral[0]};
    transform: rotate(45deg);
    content: '';
  }
`;

const SettingTooltipWrapper = styled(TooltipHintWrapper)`
  z-index: 1;
  &::after {
    left: 16px;
  }
`;

const MobileAndTabletChatPanelContainer = styled.div`
  display: none;
  ${media.tablet`
    position: absolute; // the parent has the "transform" so use absolute position to ensure the chat panel is at the bottom
    bottom: 0px;
    left: 0px;
    right: 0px;
    display: block;
  `};
`;

const ChatBackDrop = styled.div`
  position: absolute;
  top: ${headerHeight}px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  z-index: ${zIndex.backdrop};
`;

const ChatPanel = styled.div`
  position: relative;
  padding: 8px 16px
    ${isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 8}px;
  transition: max-height 0.4s ease-out;
  z-index: ${zIndex.chatPanel};
  background-color: ${color.neutral[10]};
`;

const ChatPanelInputWrapper = styled.div``;

const SuperChatPanel = styled.div`
  position: relative;
  z-index: ${zIndex.chatPanel};
`;

const ToyCommandIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  overflow: hidden;
  will-change: transform;
  transform: translateZ(0);
  z-index: 2;

  > img {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  > img:first-child {
    /* ToyCommand icon*/
    opacity: 1;
    transform: translate(-50%, -50%);
    will-change: transform, opacity;
  }
`;

export default LivestreamDetailOnlineFooterMobile;
